import { createRootRoute, Outlet } from '@tanstack/react-router'
import React, { lazy, Suspense } from 'react'

import { ToastProvider } from '#components/ToastProvider'
import { useAuth } from '#hooks/useAuth'
import { AuthProvider } from '#providers/AuthProvider'
import { LookProvider } from '#providers/LookProvider'
import { MockModeProvider } from '#providers/MockModeProvider'
import { SidebarProvider } from '#providers/SidebarProvider'
import { ThemeProvider } from '#providers/ThemeProvider'

const TanStackDevtools = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-devtools').then((module) => ({ default: module.TanStackDevtools })))
  : (): null => null

const RouterDevtoolsPanel = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((module) => ({ default: module.TanStackRouterDevtoolsPanel })),
    )
  : (): null => null

const QueryDevtoolsPanel = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-query-devtools').then((module) => ({ default: module.ReactQueryDevtoolsPanel })))
  : (): null => null

/** Gate that prevents child routes from rendering until auth initialization completes.
 *  Without this, protected routes flash briefly before AuthProvider resolves and redirects to login. */
const AuthGate = ({ children }: { children: React.ReactNode }): React.ReactNode => {
  const { isLoading } = useAuth()

  if (isLoading) return null

  return children
}

const RootComponent = (): React.JSX.Element => {
  return (
    <ThemeProvider>
      <ToastProvider />
      <AuthProvider>
        <LookProvider>
          <MockModeProvider>
            <SidebarProvider>
              <AuthGate>
                <Outlet />
              </AuthGate>
              <Suspense fallback={null}>
                <TanStackDevtools
                  plugins={[
                    {
                      name: 'TanStack Router',
                      render: <RouterDevtoolsPanel />,
                      defaultOpen: true,
                    },
                    {
                      name: 'TanStack Query',
                      render: <QueryDevtoolsPanel />,
                      defaultOpen: false,
                    },
                  ]}
                />
              </Suspense>
            </SidebarProvider>
          </MockModeProvider>
        </LookProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export const Route = createRootRoute({
  component: RootComponent,
})
